import { useState, useRef, useCallback, useEffect } from 'react';
import { useGoogleMaps } from './useGoogleMaps';

interface Address {
  en: string;
  ko: string;
}

interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export const useSearchAddress = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [englishAddress, setEnglishAddress] = useState('');
  const [koreanAddress, setKoreanAddress] = useState('');
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const { getPlaceDetails } = useGoogleMaps();

  const onLoad = useCallback((ref: any) => {
    // 이 함수는 StandaloneSearchBox의 onLoad 콜백으로 사용됩니다.
    // 실제로는 ref를 저장할 필요가 없지만, 기존 인터페이스와의 호환성을 위해 유지합니다.
  }, []);

  const searchPlaces = useCallback(async (input: string) => {
    if (!input.trim()) {
      setPredictions([]);
      setShowSuggestions(false);
      return;
    }

    const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8084'}/google-maps/places/autocomplete?input=${encodeURIComponent(input)}&language=ko`;

    try {
      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // 백엔드 응답 구조에 맞게 수정
      if (data.data && data.data.status === 'OK' && data.data.predictions) {
        setPredictions(data.data.predictions);
        setShowSuggestions(true);
      } else {
        setPredictions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error searching places:', error);
      setPredictions([]);
      setShowSuggestions(false);
    }
  }, []);

  const onPlacesChanged = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;

      // 입력값이 변경될 때마다 검색
      await searchPlaces(inputValue);
    },
    [searchPlaces]
  );

  const selectPlace = useCallback(
    async (placeId: string, description: string) => {
      setSelectedPlaceId(placeId);
      setShowSuggestions(false);

      // 입력 필드에 선택된 장소명 설정
      if (inputRef.current) {
        inputRef.current.value = description;
      }

      try {
        const [enDetails, koDetails] = await Promise.all([
          getPlaceDetails(placeId, { language: 'en' }),
          getPlaceDetails(placeId, { language: 'ko' }),
        ]);

        if (enDetails) {
          const enAddress = enDetails.name
            ? `${enDetails.name}, ${enDetails.formatted_address}`
            : enDetails.formatted_address;
          setEnglishAddress(enAddress);
        }

        if (koDetails) {
          const koAddress = koDetails.name
            ? `${koDetails.name}, ${koDetails.formatted_address}`
            : koDetails.formatted_address;
          setKoreanAddress(koAddress);
        }
      } catch (error) {
        console.error('Error getting place details:', error);
      }
    },
    [getPlaceDetails]
  );

  // 외부 클릭 시 suggestions 숨기기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return {
    onLoad,
    onPlacesChanged,
    inputRef,
    address: {
      en: englishAddress,
      ko: koreanAddress,
    } as Address,
    predictions,
    showSuggestions,
    selectPlace,
  };
};
